import * as XLSX from "xlsx";
import {
  AADTType,
  AreaType,
  AttributeDefinitions,
  AttributeName,
  AttributeSelect,
  BicycleAndLVFlowType,
  CurvatureType,
  FacilityAccessType,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  GradeType,
  HeavyVehicleFlowType,
  IntersectionApproachType,
  NumberOfLanesType,
  PedestrianFlowType,
  PresenceType,
  ProportionOfCargoBikesAndMopedsType,
  SpeedAverageType,
  SpeedDifferentialType,
} from "@keita/lanepatrol-sdk";
import { OsaNode } from "../../osaway/models/OsaNode";
import { OsaWay } from "../../osaway/models/OsaWay";
import {
  CreateOrUpdateOsaNodeCoding,
  OsaNodeCoding,
} from "../../coding/models/OsaNodeCoding";
import {
  attributeFields,
  Col,
  CsvRowArray,

  expectedCsvHeader,

  ImportCodingCsvParams,
  ImportCodingCsvResult,
} from "../models/WayCsv";

export const importCodingCsv = async ({
  file,
  selectedWay,
  nodes,
  codingsByNodeId,
}: ImportCodingCsvParams): Promise<ImportCodingCsvResult> => {
  const { headers, rows } = await parseCsv(file);

  const headerErrors = validateHeaders(headers);
  if (headerErrors.length > 0) {
    return { entries: [], errors: headerErrors };
  }

  const routeErrors = validateRouteCompatibility(rows, selectedWay);
  if (routeErrors.length > 0) {
    return { entries: [], errors: routeErrors };
  }

  const entries: CreateOrUpdateOsaNodeCoding[] = [];
  const errors: string[] = [];

  rows.forEach((values, index) => {
    if (isEmptyRow(values)) return;

    const rowNumber = index + 2;

    const node = matchNode(values, nodes);

    if (!node) {
      errors.push(
        `Row ${rowNumber}: could not match CSV row to a node in the selected route.`,
      );
      return;
    }

    const rowOptionErrors = validateRowOptionIndexes(values, rowNumber);
    if (rowOptionErrors.length > 0) {
      errors.push(...rowOptionErrors);
      return;
    }

    entries.push(buildCodingEntry(values, node, codingsByNodeId));
  });

  return { entries, errors };
};

const parseCsv = async (
  file: File,
): Promise<{
  headers: string[];
  rows: CsvRowArray[];
}> => {
  const text = await file.text();

  const workbook = XLSX.read(text, {
    type: "string",
    raw: false,
  });

  const firstSheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[firstSheetName];

  if (!sheet) {
    return { headers: [], rows: [] };
  }

  const data = XLSX.utils.sheet_to_json<(string | number | boolean | null)[]>(
    sheet,
    {
      header: 1,
      blankrows: false,
      defval: "",
      raw: false,
    },
  );

  if (data.length === 0) {
    return { headers: [], rows: [] };
  }

  const normalizedData: CsvRowArray[] = data.map((row) =>
    (row ?? []).map((cell) => (cell ?? "").toString()),
  );

  const [headerRow, ...dataRows] = normalizedData;

  return {
    headers: (headerRow ?? []).map((h) => (h ?? "").trim()),
    rows: dataRows.map((row) => row.map((cell) => cell ?? "")),
  };
};

const validateHeaders = (headers: string[]): string[] => {
  if (headers.length === 0) {
    return ["The uploaded CSV is empty."];
  }

  const errors: string[] = [];

  if (headers.length < expectedCsvHeader.length) {
    errors.push(
      `CSV has ${headers.length} columns, but at least ${expectedCsvHeader.length} were expected.`,
    );
  }

  expectedCsvHeader.forEach((expected, index) => {
    const actual = (headers[index] ?? "").trim();
    const expectedNormalized = expected.trim();
    const actualNormalized = actual.trim();

    if (actualNormalized !== expectedNormalized) {
      errors.push(
        `Header mismatch at column ${index + 1}: expected "${expected}", got "${
          headers[index] ?? ""
        }".`,
      );
    }
  });

  return errors;
};

const validateRouteCompatibility = (
  rows: CsvRowArray[],
  selectedWay: OsaWay,
): string[] => {
  const routeNames = [
    ...new Set(rows.map((r) => normalize(r[Col.routeName])).filter(Boolean)),
  ];

  if (routeNames.length === 0) {
    return ['CSV does not contain any "Cycle lane name" values.'];
  }

  if (routeNames.length > 1) {
    return [`CSV contains multiple route names: ${routeNames.join(", ")}`];
  }

  if (routeNames[0] !== normalize(selectedWay.name)) {
    return [
      `CSV route "${routeNames[0]}" does not match selected route "${selectedWay.name}".`,
    ];
  }

  return [];
};

const validateRowOptionIndexes = (
  values: CsvRowArray,
  rowNumber: number,
): string[] => {
  const errors: string[] = [];

  attributeFields.forEach(({ col, attributeName, label }) => {
    const rawValue = (values[col] ?? "").trim();

    if (rawValue === "") return;

    const numericValue = Number(rawValue);
    if (!Number.isInteger(numericValue)) {
      errors.push(
        `Row ${rowNumber}: "${label}" must be an integer option number, got "${rawValue}".`,
      );
      return;
    }

    const attribute = AttributeDefinitions.getByName(
      attributeName,
    ) as AttributeSelect;

    const maxOption = attribute.options.length;

    if (numericValue < 1 || numericValue > maxOption) {
      errors.push(
        `Row ${rowNumber}: "${label}" has invalid option "${rawValue}" Please check that the values are correct`,
      );
    }
  });

  return errors;
};

const matchNode = (values: CsvRowArray, nodes: OsaNode[]): OsaNode | null => {
  const photoReference = normalize(values[Col.photoReference]);
  const photoName = normalize(values[Col.photoName]);
  const distance = values[Col.distanceFromStart];

  if (photoReference) {
    const byReference = nodes.find(
      (node) => normalize((node as any).photo?.fileName) === photoReference,
    );
    if (byReference) return byReference;
  }

  if (photoName) {
    const byName = nodes.find(
      (node) => normalize((node as any).name) === photoName,
    );
    if (byName) return byName;
  }

  if (distance !== null) {
    const byDistance = nodes.find((node) => {
      return distance;
    });
    if (byDistance) return byDistance;
  }

  return null;
};

const buildCodingEntry = (
  values: CsvRowArray,
  node: OsaNode,
  codingsByNodeId: Record<string, OsaNodeCoding>,
): CreateOrUpdateOsaNodeCoding => {
  const existingCoding = codingsByNodeId[node.id];

  return {
    id: existingCoding?.id ?? null,
    osaNodeId: node.id,
    suggestions: existingCoding?.suggestions ?? [],
    attributes: {
      comment: values[Col.comment],

      areaType: getOptionValue(
        AttributeName.AreaType,
        values[Col.areaType],
      ) as AreaType,

      facilityType: getOptionValue(
        AttributeName.FacilityType,
        values[Col.facilityType],
      ) as FacilityType,

      facilityAccess: getOptionValue(
        AttributeName.FacilityAccess,
        values[Col.facilityAccess],
      ) as FacilityAccessType,

      looseOrSlipperySurface: getOptionValue(
        AttributeName.LooseOrSlipperySurface,
        values[Col.looseOrSlipperySurface],
      ) as PresenceType,

      tramOrTrainRails: getOptionValue(
        AttributeName.TramOrTrainRails,
        values[Col.tramOrTrainRails],
      ) as PresenceType,

      majorSurfaceDeformationOrDrainOpening: getOptionValue(
        AttributeName.MajorSurfaceDeformationOrDrainOpening,
        values[Col.majorSurfaceDeformationOrDrainOpening],
      ) as PresenceType,

      fixedObstacleOnFacility: getOptionValue(
        AttributeName.FixedObstacleOnFacility,
        values[Col.fixedObstacleOnFacility],
      ) as PresenceType,

      nonFixedObstacleOnFacility: getOptionValue(
        AttributeName.NonFixedObstacleOnFacility,
        values[Col.nonFixedObstacleOnFacility],
      ) as PresenceType,

      delineation: getOptionValue(
        AttributeName.Delineation,
        values[Col.delineation],
      ) as PresenceType,

      lightSegregation: getOptionValue(
        AttributeName.LightSegregation,
        values[Col.lightSegregation],
      ) as PresenceType,

      facilityWidthPerDirection: getOptionValue(
        AttributeName.FacilityWidthPerDirection,
        values[Col.facilityWidthPerDirection],
      ) as FacilityWidth,

      flowDirection: getOptionValue(
        AttributeName.FlowDirection,
        values[Col.flowDirection],
      ) as FlowDirectionType,

      widthRestriction: getOptionValue(
        AttributeName.WidthRestriction,
        values[Col.widthRestriction],
      ) as PresenceType,

      adjacentFeatures0to1m: {
        roadLane: getOptionValue(
          AttributeName.AdjacentFeatures0to1mRoadLane,
          values[Col.roadLane01],
        ) as PresenceType,
        vehicleParking: getOptionValue(
          AttributeName.AdjacentFeatures0to1mVehicleParking,
          values[Col.vehicleParking01],
        ) as PresenceType,
        severeHazard: getOptionValue(
          AttributeName.AdjacentFeatures0to1mSevereHazard,
          values[Col.severeHazard01],
        ) as PresenceType,
        objectOrLevelChange: getOptionValue(
          AttributeName.AdjacentFeatures0to1mObjectOrLevelChange,
          values[Col.objectOrLevelChange01],
        ) as PresenceType,
        sidewalk: getOptionValue(
          AttributeName.AdjacentFeatures0to1mSidewalk,
          values[Col.sidewalk01],
        ) as PresenceType,
      },

      adjacentFeatures1to3m: {
        roadLane: getOptionValue(
          AttributeName.AdjacentFeatures1to3mRoadLane,
          values[Col.roadLane13],
        ) as PresenceType,
        vehicleParking: getOptionValue(
          AttributeName.AdjacentFeatures1to3mVehicleParking,
          values[Col.vehicleParking13],
        ) as PresenceType,
        severeHazard: getOptionValue(
          AttributeName.AdjacentFeatures1to3mSevereHazard,
          values[Col.severeHazard13],
        ) as PresenceType,
        objectOrLevelChange: getOptionValue(
          AttributeName.AdjacentFeatures1to3mObjectOrLevelChange,
          values[Col.objectOrLevelChange13],
        ) as PresenceType,
        sidewalk: getOptionValue(
          AttributeName.AdjacentFeatures1to3mSidewalk,
          values[Col.sidewalk13],
        ) as PresenceType,
      },

      grade: getOptionValue(
        AttributeName.Grade,
        values[Col.grade],
      ) as GradeType,

      curvature: getOptionValue(
        AttributeName.Curvature,
        values[Col.curvature],
      ) as CurvatureType,

      streetLighting: getOptionValue(
        AttributeName.StreetLighting,
        values[Col.streetLighting],
      ) as PresenceType,

      pedestrianCrossing: getOptionValue(
        AttributeName.PedestrianCrossing,
        values[Col.pedestrianCrossing],
      ) as PresenceType,

      intersectionBicycleFacility: getOptionValue(
        AttributeName.IntersectionBicycleFacility,
        values[Col.intersectionBicycleFacility],
      ) as PresenceType,

      intersectionApproach: getOptionValue(
        AttributeName.IntersectionApproach,
        values[Col.intersectionApproach],
      ) as IntersectionApproachType,

      intersectionOrRoadCrossing: getOptionValue(
        AttributeName.IntersectionOrRoadCrossing,
        values[Col.intersectionOrRoadCrossing],
      ) as PresenceType,

      crossingFacility: getOptionValue(
        AttributeName.CrossingFacility,
        values[Col.crossingFacility],
      ) as PresenceType,

      numberOfLanesAdjacentRoad: getOptionValue(
        AttributeName.NumberOfLanesAdjacentRoad,
        values[Col.numberOfLanesAdjacentRoad],
      ) as NumberOfLanesType,

      numberOfLanesIntersectingRoad: getOptionValue(
        AttributeName.NumberOfLanesIntersectingRoad,
        values[Col.numberOfLanesIntersectingRoad],
      ) as NumberOfLanesType,

      propertyAccess: getOptionValue(
        AttributeName.PropertyAccess,
        values[Col.propertyAccess],
      ) as PresenceType,

      peakPedestrianFlowAlongOrAcrossFacility: getOptionValue(
        AttributeName.PeakPedestrianFlowAlongOrAcrossFacility,
        values[Col.peakPedestrianFlowAlongOrAcrossFacility],
      ) as PedestrianFlowType,

      peakBicycleAndLVTrafficFlow: getOptionValue(
        AttributeName.PeakBicycleAndLVTrafficFlow,
        values[Col.peakBicycleAndLVTrafficFlow],
      ) as BicycleAndLVFlowType,

      observedProportionOfCargoBikesAndMopeds: getOptionValue(
        AttributeName.ObservedProportionOfCargoBikesAndMopeds,
        values[Col.observedProportionOfCargoBikesAndMopeds],
      ) as ProportionOfCargoBikesAndMopedsType,

      bicycleAndLVSpeedAverage: getOptionValue(
        AttributeName.BicycleAndLVSpeedAverage,
        values[Col.bicycleAndLVSpeedAverage],
      ) as SpeedAverageType,

      bicycleAndLVSpeedDifferential: getOptionValue(
        AttributeName.BicycleAndLVSpeedDifferential,
        values[Col.bicycleAndLVSpeedDifferential],
      ) as SpeedDifferentialType,

      roadAADT: getOptionValue(
        AttributeName.RoadAADT,
        values[Col.roadAADT],
      ) as AADTType,

      heavyVehicleFlow: getOptionValue(
        AttributeName.HeavyVehicleFlow,
        values[Col.heavyVehicleFlow],
      ) as HeavyVehicleFlowType,

      roadSpeedLimit: values[Col.roadSpeedLimit]
        ? Number(values[Col.roadSpeedLimit])
        : null,

      roadOperatingSpeed: values[Col.roadOperatingSpeed]
        ? Number(values[Col.roadOperatingSpeed])
        : null,
    },
  };
};

const getOptionValue = (
  attributeName: AttributeName,
  indexStr: string | undefined,
): string | null => {
  if (!indexStr) return null;

  const index = Number(indexStr);
  if (!Number.isInteger(index) || index < 1) return null;

  const attribute = AttributeDefinitions.getByName(
    attributeName,
  ) as AttributeSelect;

  return attribute.options[index - 1]?.value ?? null;
};

const normalize = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

const isEmptyRow = (values: CsvRowArray): boolean =>
  values.every((v) => (v ?? "").trim() === "");

export { Col, expectedCsvHeader };
